import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Sidebar } from 'sa-ui';
import { getSidebarRoutes } from './appHelpers';
import AppSidebarItem from './AppSidebarItem';

import './AppSidebar.scss';

import messages from './appMessages';

function snapshotRoutesForSidebar(source) {
  return source.map((r) => ({
    ...r,
    childRoutes: r.childRoutes ? r.childRoutes.map((cr) => ({ ...cr })) : r.childRoutes,
  }));
}

function AppSidebar({
  routes,
  collapsed,
  showReports,
  showReportsTRI,
  hasEvaluationWithEssay,
  canEditSchool,
  hasAccessToDemoSchool,
  coordinator,
  coordinatorPermission,
  logsNotRead,
  userType,
}) {
  const [sidebarRoutes, setSidebarRoutes] = useState([]);
  const prevRoutes = useRef(routes);

  useEffect(() => {
    const routesWorking = snapshotRoutesForSidebar(routes);

    const sidebarCtx = {
      showReports,
      showReportsTRI,
      coordinator,
      userType,
      hasEvaluationWithEssay,
      canEditSchool,
      hasAccessToDemoSchool,
      coordinatorPermission,
    };

    let allowedRoutes = routesWorking.filter((r) => r.show === undefined || r.show(sidebarCtx));

    allowedRoutes.forEach((r) => {
      if (Array.isArray(r.childRoutes)) {
        r.childRoutes = r.childRoutes.filter((r2) => r2.show === undefined || r2.show(sidebarCtx));
      }
    });

    if (showReports) {
      allowedRoutes.forEach((r) => {
        if (r.path === '/reports' && Array.isArray(r.childRoutes)) {
          const existingRouteApplication = r.childRoutes.find(
            (route) => route.path === '/reports/application',
          );
          if (!existingRouteApplication) {
            r.childRoutes.unshift({
              path: '/reports/application',
              active: (location) => /^\/reports\/application/.test(location.pathname),
              label: <FormattedMessage {...messages.applications} />,
              show: ({ showReports, userType }) => showReports && userType !== 4,
              allow: ({ showReports, userType }) => showReports && userType !== 4,
            });
          }

          const existingRouteGeneral = r.childRoutes.find(
            (route) => route.path === '/reports/general',
          );
          if (!existingRouteGeneral) {
            r.childRoutes.unshift({
              path: '/reports/general',
              active: (location) => /^\/reports\/general/.test(location.pathname),
              label: <FormattedMessage {...messages.sidebarGeneralPanel} />,
              show: ({ showReports, userType }) => showReports && userType !== 4,
              allow: ({ showReports, userType }) => showReports && userType !== 4,
            });
          }
        }
      });
    }

    if (showReportsTRI) {
      allowedRoutes.forEach((r) => {
        if (r.path === '/reports' && Array.isArray(r.childRoutes)) {
          // Verifica se o caminho já existe antes de adicionar
          const existingRoute = r.childRoutes.find((route) => route.path === '/reports/gptri');
          if (!existingRoute) {
            r.childRoutes.push({
              path: '/reports/gptri',
              active: (location) => /^\/reports\/gptri/.test(location.pathname),
              label: <FormattedMessage {...messages.generalPanelTRI} />,
              show: ({ showReportsTRI, userType }) => showReportsTRI && userType !== 4,
              allow: ({ showReportsTRI, userType }) => showReportsTRI && userType !== 4,
            });
          }
        }
      });
    }

    const canAccessExecutivePanel =
      userType != null &&
      Number(userType) !== 4 &&
      [1, 2].includes(Number(coordinatorPermission));

    if (!hasEvaluationWithEssay && !showReports && !canAccessExecutivePanel) {
      allowedRoutes = allowedRoutes.filter((r) => r.path !== '/reports');
    }

    setSidebarRoutes(getSidebarRoutes(allowedRoutes));
    prevRoutes.current = routes;
  }, [
    routes,
    showReports,
    showReportsTRI,
    coordinator,
    coordinatorPermission,
    userType,
    hasEvaluationWithEssay,
    canEditSchool,
    hasAccessToDemoSchool,
  ]);

  return (
    <Sidebar collapsed={collapsed}>
      {sidebarRoutes.map(({ sidebar, ...routeProps }) => {
        return <AppSidebarItem {...sidebar} {...routeProps} logsNotRead={logsNotRead} />;
      })}
    </Sidebar>
  );
}

AppSidebar.propTypes = {
  collapsed: PropTypes.bool,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      sidebar: PropTypes.shape({ icon: PropTypes.node, label: PropTypes.node }),
    }),
  ),
};

AppSidebar.defaultProps = {
  collapsed: false,
  routes: [],
};

function mapStateToProps({ login, app, controlPanel }) {
  return {
    showReports: login.showReports,
    showReportsTRI: login.showReportsTRI,
    hasEvaluationWithEssay: app.user ? app.user.hasEvaluationWithEssay : null,
    canEditSchool: app.canEditSchool,
    hasAccessToDemoSchool: app.hasAccessToDemoSchool,
    coordinator: app.coordinator,
    coordinatorPermission: login.isCoordinator ? login.isCoordinator.permission : null,
    logsNotRead: controlPanel.logsNotRead,
    userType: app.user ? app.user.idType : null,
  };
}

export default connect(mapStateToProps)(AppSidebar);
